import _defineProperty from "@babel/runtime/helpers/defineProperty";
import "@abinitio/kendo.all";
import { DateTimeUtils } from "abweb/utils/date/DateTimeUtils";
import "abweb/utils/date/internal/DateUtilsInternals"; // side-effect only (gross!)
import { LogUtils } from "abweb/utils/log/LogUtils";

// Below support metadata for Profile Stats gelato grid, not used anymore so remove...
// import {UIMetaDataImpl} from "abweb/dto/data/UIMetaDataImpl";
// import {AbWebRenderName} from "abweb/utils/profile/AbWebRenderName";

export class ProfileUtils {
  constructor() {
    // static class, can't create an instance
  }
  static get enabled() {
    return ProfileUtils._enabled;
  }
  static set enabled(val) {
    ProfileUtils._enabled = val;
  }
  static get logStatsEnabled() {
    return this._logStatsEnabled;
  }
  static set logStatsEnabled(value) {
    this._logStatsEnabled = value;
  }
  static reset() {
    ProfileUtils.statEntries.clear();
    ProfileUtils.LOG.debug("Reset stats");
  }
  static getProfileStats() {
    const profStats = [];
    ProfileUtils.statEntries.forEach(val => profStats.push(val));
    return profStats;
  }
  static logProfileStats(header, filter) {
    let ps = ProfileUtils.getProfileStats();
    if (filter) {
      ps = ps.filter(stat => stat.label.indexOf(filter) >= 0);
    }
    const sb = [header];
    for (const stat of ps) {
      sb.push(stat.toString());
    }
    const msg = sb.join("\n   ");
    ProfileUtils.LOG.info(msg); // do not log at debug level or won't show up in tests...
    return msg;
  }

  // Commented out usage in AuthenticationService.logout (uncomment to debug logout rpc race conditions)
  static logInFlightRPCs(header) {
    let ps = ProfileUtils.getProfileStats();
    ps = ps.filter(stat => stat.numActive > 0 && stat.label.indexOf("RPCService:") >= 0
    // Only want the invoke stat for an rpc (don't care about parseToJson, responseBodyLength, ...)
    && stat.label.indexOf(":invoke") >= 0);
    const sb = [header];
    for (const stat of ps) {
      sb.push(stat.toString(false));
    }
    const msg = sb.join("\n   ");
    ProfileUtils.LOG.info(msg); // do not log at debug level or won't show up in tests...
    return msg;
  }
  static profileStatsToCSV(filter) {
    const header = `"Label","Hits","Avg Value","Total Value","StdDev Value","Last Value","Min Value","Max Value",` + `"Current Active","Avg Active","Max Active","First Access","Last Access"`;
    let ps = ProfileUtils.getProfileStats();
    if (filter) {
      ps = ps.filter(stat => stat.label.indexOf(filter) >= 0);
    }
    const sb = [header];
    for (const stat of ps) {
      sb.push(stat.toCSV());
    }
    return sb.join("\n");
  }

  // ++++++++++ MonitorFactory Methods +++++++++++++++++++++

  /**
   * Add arbitrary stat to monitored results.
   * Think carefully about the label contents, as will use it in sorting and filtering to
   * drill down to interesting set of stats.
   * Example: add('litebrite:configValueView:numRows", "count", rows.length)
   */
  static add(label, units, value) {
    if (ProfileUtils.enabled) {
      const profileStats = ProfileUtils.getOrBuildProfileStats(label, units);
      profileStats.addValue(value); // don't bother increment/decrement of active...
      if (ProfileUtils.logStatsEnabled) {
        profileStats.logStats();
      }
    }
  }

  /**
   * Collect elapsed time stats for invoking the synchronous fn (use elapsedAsync if it an an asynchronous function)
   * @param {string | (() => string)} label  The profile stat label (or function that returns the label)
   * @param {() => RetT} fn The fn to be profiled
   * @return {RetT} returns the result of executing the function
   */
  static elapsedSync(label, fn) {
    if (!ProfileUtils.enabled) {
      return fn();
    }
    const mon = ProfileUtils.startMonitor(label);
    try {
      return fn();
    } finally {
      mon.stop();
    }
  }

  /**
   * Collect elapsed time stats for invoking the asynchronous fn (use elapsedSync if it an a synchronous function)
   * @param {string | (() => string)} label  The profile stat label (or function that returns the label)
   * @param {() => RetT} promiseFn The async function to be profiled
   * @return {RetT} returns the result of executing the promiseFn
   */
  static async elapsedAsync(label, promiseFn) {
    if (!ProfileUtils.enabled) {
      return promiseFn();
    }
    const promiseMon = ProfileUtils.startMonitor(label);
    try {
      // await is important as need to measure time until promise returned from promiseFn settles (resolve | reject)
      return await promiseFn();
    } finally {
      promiseMon.stop();
    }
  }

  // ++++++++++ Stat Entry Methods +++++++++++++++++++++
  /**
   * Return non-null Monitor, which caller will subsequently invoke 'stop()' on to record the timing stats.
   * Think carefully about the label contents, as will use it in sorting and filtering to
   * drill down to interesting set of stats.
   * @param label
   * @return {Monitor}
   */
  static startMonitor(label) {
    const monStat = ProfileUtils.enabled ? ProfileUtils.getOrBuildProfileStats(label, "ms") : null; // if monStat is null, monitor.stop is a no-op (caller doesn't care if monitoring disabled...)
    return new Monitor(monStat);
  }
  static getOrBuildProfileStats(label, units) {
    const labelStr = typeof label === "string" ? label : label();
    const key = labelStr + ", " + units;
    let retVal = ProfileUtils.statEntries.get(key);
    if (retVal == null) {
      if (ProfileUtils.statEntries.size >= ProfileUtils.MAX_NUM_PROFILED_STATS) {
        // Big hammer to ensure bad profiling usage doesn't blow out memory due to unconstrained statEntries size.
        // Labels typically shouldn't have parameterized values within them as get unconstrained stats growth!
        ProfileUtils.LOG.warn("Resetting all profiling stats as exceeded max size of " + ProfileUtils.MAX_NUM_PROFILED_STATS);
        ProfileUtils.reset(); // big hammer, do not want LRU overhead, shouldn't happen in practice (sort by lastAccess?)
      }
      retVal = new ProfileStats(key);
      ProfileUtils.statEntries.set(key, retVal);
    }
    return retVal;
  }
}
_defineProperty(ProfileUtils, "LOG", LogUtils.getLogger(__filename));
_defineProperty(ProfileUtils, "MAX_NUM_PROFILED_STATS", 10000);
// will reset if gets to be more than this!
_defineProperty(ProfileUtils, "statEntries", new Map());
_defineProperty(ProfileUtils, "_enabled", true);
_defineProperty(ProfileUtils, "_logStatsEnabled", true);
class Monitor {
  // only populated when have partial performance measure support

  constructor(stats) {
    this.stats = stats;
    if (this.stats) {
      this.stats.incrementActive();
      // Do NOT use performance.mark to get the start/end of the duration as
      // is visually redundant with the start/end of the 'measure' horizontal bar in the Performance -> Timings section
      this.startMs = performance.now(); // performance.now() preferred over Date.now()
      if (!Monitor.FullPerformanceMeasureSupport) {
        this.startMarkLabel = `start:${this.stats.label}:${Monitor.MarkGuid++}`;
        performance.mark(this.startMarkLabel);
      }
      if (ProfileUtils.logStatsEnabled) {
        Monitor.LOG.debug("Starting monitor " + this.stats.label);
      }
    }
  }
  stop() {
    if (this.stats)
      // will be null when ProfileUtils.enabled==false at start
      {
        const durMs = performance.now() - this.startMs;
        const startOrMeasureOptions = Monitor.FullPerformanceMeasureSupport
        // Preferred approach as do not require start 'performance.mark' that is redundant with the
        // start of the 'measure bar' in the browser debugger timings display
        ? {
          start: this.startMs,
          duration: durMs
        }
        // FF < v103 does not support startOrMeasureOptions (should be very close to calculated durMs)
        : this.startMarkLabel;
        performance.measure(this.stats.label, startOrMeasureOptions);
        this.stats.addValue(durMs);
        this.stats.decrementActive();
        if (ProfileUtils.logStatsEnabled) {
          this.stats.logStats();
        }
      }
  }
}
_defineProperty(Monitor, "LOG", LogUtils.getLogger(__filename));
// logs as ProfileUtils
_defineProperty(Monitor, "MarkGuid", 1);
_defineProperty(Monitor, "FullPerformanceMeasureSupport", function calcIfPerformanceMeasureSupported() {
  try {
    performance.measure("DeterminePerformanceMeasureSupport", {
      start: 0,
      duration: 1
    });
    console.log("Performance.measure fully supported.");
    return true;
  } catch (err) {
    console.log("Performance.measure partially supported.");
    return false;
  }
}());
export class ProfileStats extends kendo.data.ObservableObject {
  // last stat completed

  constructor(label) {
    super();
    // logs as ProfileUtils
    /*
      private static readonly _FIELD_METADATA: UIMetaDataImpl = UIMetaDataImpl.factoryForClass(
          ProfileStats, "label",
          [
            {fieldKey: "label", fieldType: "string"}, // Use default renderer for type
            {fieldKey: "valueHits", fieldType: "number", columnRendererName: AbWebRenderName.INTEGER},
            {fieldKey: "avgValue", fieldType: "number", columnRendererName: AbWebRenderName.DECIMAL_3},
            {fieldKey: "totalValue", fieldType: "number", columnRendererName: AbWebRenderName.DECIMAL_3},
            {fieldKey: "stdDevValue", fieldType: "number", columnRendererName: AbWebRenderName.DECIMAL_3},
            {fieldKey: "lastValue", fieldType: "number", columnRendererName: AbWebRenderName.DECIMAL_3},
            {fieldKey: "minValue", fieldType: "number", columnRendererName: AbWebRenderName.DECIMAL_3},
            {fieldKey: "maxValue", fieldType: "number", columnRendererName: AbWebRenderName.DECIMAL_3},
            {fieldKey: "numActive", fieldType: "number", columnRendererName: AbWebRenderName.DECIMAL_3},
            {fieldKey: "avgActive", fieldType: "number", columnRendererName: AbWebRenderName.DECIMAL_3},
            {fieldKey: "maxActive", fieldType: "number", columnRendererName: AbWebRenderName.INTEGER},
            {fieldKey: "firstAccess", fieldType: Date, columnRendererName: AbWebRenderName.DATE_TIMESTAMP},
            {fieldKey: "lastAccess", fieldType: Date, columnRendererName: AbWebRenderName.DATE_TIMESTAMP}
          ]);
    
      public static getDefaultFieldMetaData(): UIMetaDataImpl/!*IRectangularMetaData*!/
      {
        return ProfileStats._FIELD_METADATA;
      }
    */
    _defineProperty(this, "_label", void 0);
    // includes appended uom, such as ', ms' or ', count'
    _defineProperty(this, "_numActive", 0);
    _defineProperty(this, "_activeHits", 0);
    _defineProperty(this, "_totalActive", 0);
    _defineProperty(this, "_maxActive", 0);
    // _avgActive: number is lazily calculated on demand (when stats are rendered)
    // All stats below do not reflect any 'active' (in progress) stat collection
    _defineProperty(this, "_valueHits", 0);
    _defineProperty(this, "_lastValue", 0);
    _defineProperty(this, "_totalValue", 0);
    _defineProperty(this, "_sumOfValueSquared", 0);
    _defineProperty(this, "_minValue", Number.MAX_VALUE);
    _defineProperty(this, "_maxValue", Number.MIN_VALUE);
    // avgValue and stdDevValue are lazily calculate on demand (when stats are rendered)
    _defineProperty(this, "_firstAccess", void 0);
    // first stat completed
    _defineProperty(this, "_lastAccess", void 0);
    this._label = label;
  }
  incrementActive() {
    this._numActive++; // current number of active methods (timings)
    this._activeHits++; // cumulative
    this._maxActive = Math.max(this._maxActive, this._numActive);
    this._totalActive += this._numActive;
  }
  decrementActive() {
    this._numActive--;
  }
  addValue(val) {
    this._valueHits++;
    this._lastValue = val;
    this._minValue = Math.min(this._minValue, val);
    this._maxValue = Math.max(this._maxValue, val);
    this._totalValue += val; // used in avg and stdddev calc
    this._sumOfValueSquared += val * val; // used in stdddev calc

    if (this._firstAccess == null) {
      this._firstAccess = new Date();
      this._lastAccess = this._firstAccess;
    } else {
      this._lastAccess = new Date();
    }
  }
  toString(summaryStats = true) {
    // Only output important (value) stats by default
    return `Label: ${this._label}, Hits: ${this._valueHits}, Avg: ${this.avgValue}, StdDev: ${this.stdDevValue},` + ` Total: ${this._totalValue}, LastValue: ${this._lastValue},` + ` MinValue: ${this._minValue}, MaxValue: ${this._maxValue}` + (summaryStats ? "" : ` NumActive: ${this._numActive}, AvgActive: ${this.avgActive}, MaxActive: ${this._maxActive}` + ` FirstAccess: ${this.formatCsv(this._firstAccess)}, LastAccess: ${this.formatCsv(this._lastAccess)}`);
  }
  formatCsv(date) {
    // At one point got cypress test iframe cyclic dependency when DTU dynamically imported DateUtilsInternally.
    // Fix was add the below static import at the top of this file.
    // Could experiment with putting side-effect import at top of ab_cy.ts instead
    //      import "abweb/utils/date/internal/DateUtilsInternals"; // side-effect only (gross!)
    return date ? DateTimeUtils.FORMAT_TIMESTAMP_CSV.format(date) : ""; // 10/31/22 14:15:16

    // Below totally removed dependency on DTU, BUT still had cypress test iframe cyclic dep
    // if (!date) {
    //   return "";
    // }
    // const twoDigYear = date.getFullYear() % 100; // last 2 digits
    // return `${date.getMonth() + 1}/${date.getDay()}/${twoDigYear} ${date.getHours()}:${date.getMinutes()}:${date.getSeconds()}`;
  }
  toCSV() {
    const quotedLabel = '"' + this._label.replace(/"/g, '""') + '"';
    return `${quotedLabel},${this._valueHits},` + `${this.avgValue},${this._totalValue},${this.stdDevValue},${this._lastValue},${this._minValue},${this._maxValue},` + `${this._numActive},${this.avgActive},${this._maxActive},` + `${this.formatCsv(this._firstAccess)},${this.formatCsv(this._lastAccess)}`;
  }
  logStats() {
    ProfileStats.LOG.debug(this.toString());
  }

  ///////// Public stats ///////////

  get label() {
    return this._label;
  }
  get numActive() {
    return this._numActive;
  }
  get maxActive() {
    return this._maxActive;
  }
  get avgActive() {
    return this._activeHits > 0 ? this._totalActive / this._activeHits : 0;
  }
  get valueHits() {
    return this._valueHits;
  }
  get lastValue() {
    return this._lastValue;
  }
  get totalValue() {
    return this._totalValue;
  }
  get minValue() {
    return this._minValue;
  }
  get maxValue() {
    return this._maxValue;
  }
  get firstAccess() {
    return this._firstAccess;
  }
  get lastAccess() {
    return this._lastAccess;
  }
  get avgValue() {
    return this._valueHits > 0 ? this._totalValue / this._valueHits : 0;
  }

  // From server-side Jamon Monitor stddev implementation (client side should be consistent)
  // if(this.monData.hits != 0.0D) {
  //   double sumOfX = this.monData.total;
  //   double n = this.monData.hits;
  //   double nMinus1 = n <= 1.0D?1.0D:n - 1.0D;
  //   double numerator = this.monData.sumOfSquares - sumOfX * sumOfX / n;
  //   return  Math.sqrt(numerator / nMinus1); // std dev
  //   }
  //   else
  //   return 0; // stddev
  get stdDevValue() {
    if (this._valueHits < 2) {
      return 0;
    }
    const numerator = this._sumOfValueSquared - this._totalValue * this._totalValue / this._valueHits;
    return Math.sqrt(numerator / (this._valueHits - 1));
  }
}
_defineProperty(ProfileStats, "LOG", LogUtils.getLogger(__filename));
//# sourceMappingURL=ProfileUtils.js.map
